import { Link } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  category: string;
  date: string;
  author: string;
  readTime: number;
  tags: string[];
  featured: boolean;
}

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link to={`/articles/${article.id}`} className="group block cursor-pointer">
        <div className="relative overflow-hidden rounded-lg aspect-[16/9] bg-background-100">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="mt-4">
          <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="mt-3 text-lg md:text-xl font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
            <span>{article.date}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/articles/${article.id}`} className="group flex gap-4 cursor-pointer">
        <div className="w-24 h-24 flex-shrink-0 overflow-hidden rounded-lg bg-background-100">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-400">
            <span>{article.category}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/articles/${article.id}`} className="group block cursor-pointer">
      <div className="relative overflow-hidden rounded-lg aspect-[4/3] bg-background-100">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 text-foreground-800 text-xs font-medium rounded-full whitespace-nowrap">
          {article.category}
        </span>
      </div>
      <div className="mt-3">
        <h3 className="text-base font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
          <span>{article.author}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span>{article.date}</span>
          <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
          <span>{article.readTime} 分钟阅读</span>
        </div>
      </div>
    </Link>
  );
}